import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { IProcedure } from 'src/app/models/procedure.model';
import { IError } from 'src/app/models/error.model';
import { ProcedureService } from 'src/app/services/procedure.service';
import { setWarningText } from 'src/app/reducers/warning-popup.reducer';

@Component({
  selector: 'app-procedure-edit-v2',
  templateUrl: './procedure-edit.component.html',
  styleUrls: ['./procedure-edit.component.sass']
})
export class ProcedureEditV2Component implements OnInit {
  loading = false;
  procedureForm!: FormGroup;
  procedureId: string | null = '';
  charRemaining = 250;

  constructor(
    private fb: FormBuilder,
    private procedureService: ProcedureService,
    private router: Router,
    private route: ActivatedRoute,
    private store: Store<{ warningPopup: IError }>
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.route.paramMap.subscribe(params => {
      this.procedureId = params.get('procedureId');
      if (this.procedureId) {
        this.loadProcedure();
      }
    });
  }

  initForm(): void {
    this.procedureForm = this.fb.group({
      description: ['', [Validators.required, Validators.maxLength(250)]],
      value: ['R$ 0,00', [Validators.required]],
      paymentMethod: ['']
    });

    // Atualiza contador de caracteres
    this.procedureForm.get('description')?.valueChanges.subscribe(value => {
      this.charRemaining = 250 - (value?.length || 0);
    });
  }

  loadProcedure(): void {
    this.loading = true;
    this.procedureService.getProcedure(this.procedureId!).subscribe({
      next: (procedure: IProcedure) => {
        // Formata o valor no padrão Mercado Pago
        const valueFormatted = this.formatCurrencyFromNumber(procedure.value || 0);
        
        this.procedureForm.patchValue({
          description: procedure.description,
          value: valueFormatted,
          paymentMethod: procedure.payment_method
        });
        this.charRemaining = 250 - (procedure.description?.length || 0);
        this.loading = false;
      },
      error: (error) => {
        console.error('Erro ao carregar procedimento:', error);
        this.loading = false;
      }
    });
  }

  /**
   * Formata um número para o padrão monetário brasileiro
   */
  formatCurrencyFromNumber(value: number): string {
    const valueStr = Math.round(value * 100).toString();
    return this.formatCurrencyAsMercadoPago(valueStr);
  }

  onKeydown(event: KeyboardEvent): void {
    // Bloqueia vírgulas
    if (event.key === ',') {
      event.preventDefault();
    }
  }

  // Método para processar a digitação no campo de valor
  onValueInput(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    // Remove tudo que não for número
    const numericValue = inputElement.value.replace(/\D/g, '');
    
    // Formatação estilo Mercado Pago
    let formattedValue = this.formatCurrencyAsMercadoPago(numericValue);
    
    // Atualiza o input e o FormControl
    inputElement.value = formattedValue;
    this.procedureForm.get('value')?.setValue(formattedValue, { emitEvent: false });
  }

  /**
   * Formata valor numérico no padrão do Mercado Pago
   * Digitação da direita para esquerda, começando pelos centavos
   */
  formatCurrencyAsMercadoPago(value: string): string {
    // Garante que o valor seja uma string e remove caracteres não numéricos
    const rawValue = value.toString().replace(/\D/g, '');
    
    // Se estiver vazio, retorna zero formatado
    if (!rawValue || rawValue === '') {
      return 'R$ 0,00';
    }
    
    // Adiciona zeros à esquerda se necessário para garantir pelo menos 3 caracteres
    // (1 para real e 2 para centavos)
    const paddedValue = rawValue.padStart(3, '0');
    
    // Pega os dois últimos dígitos para centavos
    const centavos = paddedValue.slice(-2);
    
    // O resto são reais
    let reais = paddedValue.slice(0, -2);
    
    // Remove zeros à esquerda nos reais (mas mantém um zero se o valor for zero)
    reais = reais.replace(/^0+/, '') || '0';
    
    // Formata reais com pontos para milhares
    const formattedReais = this.formatThousands(reais);
    
    // Retorna o valor formatado completo
    return `R$ ${formattedReais},${centavos}`;
  }
  
  /**
   * Adiciona pontos como separadores de milhar
   */
  formatThousands(value: string): string {
    if (!value || value === '' || value === '0') {
      return '0';
    }
    
    // Adiciona pontos como separadores de milhar
    return value.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  }

  // Método para formatar o valor quando o campo perder o foco
  onValueBlur(event: Event): void {
    // Apenas para garantir que o valor está formatado corretamente
    const inputElement = event.target as HTMLInputElement;
    const inputValue = inputElement.value;
    
    // Se o campo estiver vazio, coloca zero formatado
    if (!inputValue || inputValue === '') {
      const formattedValue = 'R$ 0,00';
      inputElement.value = formattedValue;
      this.procedureForm.get('value')?.setValue(formattedValue, { emitEvent: false });
    }
    // Se não estiver no formato correto, reformata
    else if (!inputValue.startsWith('R$ ') || !inputValue.includes(',')) {
      const numericValue = inputValue.replace(/\D/g, '');
      const formattedValue = this.formatCurrencyAsMercadoPago(numericValue);
      inputElement.value = formattedValue;
      this.procedureForm.get('value')?.setValue(formattedValue, { emitEvent: false });
    }
  }

  onCancel(): void {
    this.router.navigate(['main', 'v2', 'procedures']);
  }

  onSubmit(): void {
    if (this.procedureForm.invalid) {
      this.procedureForm.markAllAsTouched();
      this.store.dispatch(setWarningText('Preencha e valide todos os campos obrigatórios.'));
      return;
    }

    this.loading = true;
    const formValue = this.procedureForm.value;

    // Converte valor para número (remove R$, pontos e substitui vírgula por ponto)
    let valueNum = 0;
    if (formValue.value) {
      const valueStr = formValue.value.toString()
        .replace('R$ ', '')
        .replace(/\./g, '')
        .replace(',', '.');
      valueNum = parseFloat(valueStr);
    }

    const procedureRequest: IProcedure = {
      description: formValue.description?.trim() || '',
      value: valueNum,
      payment_method: formValue.paymentMethod?.trim() || ''
    };

    this.procedureService.updateProcedure(this.procedureId || '', procedureRequest).subscribe({
      next: () => {
        this.loading = false;
        this.router.navigateByUrl('/main/v2', { skipLocationChange: true }).then(() => {
          this.router.navigate(['main', 'v2', 'procedures']);
        });
      },
      error: (error) => {
        console.error('Erro ao atualizar procedimento:', error);
        this.loading = false;
      }
    });
  }
}
